import r2wc from "@r2wc/react-to-web-component";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MemoryRouter } from "react-router-dom";
import "./styles/host-reset.scss";
import "./styles/App.scss";
import "./styles/manage-order.scss";
import "./styles/tailwind.css";

import "./styles/form.scss";
import "./styles/confirm-payment-modal.scss";
import "./styles/booking-form-mfe.scss";
import "../i18n";

import BookingForm from "./components/bookingForm/BookingForm";
import { BookingFormLauncher } from "./components/bookingForm/BookingFormLauncher";
import { BookingFormMaximiseButton } from "./components/bookingForm/BookingFormMaximiseButton";
import ConfirmPayment from "./components/ConfirmPayment";
import { Modal } from "./components/ui/Modal";
import { initialiseBugsnag } from "./bugsnag";
import { parseCssVariables } from "./utils/parseCssVariables";
import { THEMED_SURFACE_CLASS, useThemedSurface } from "./components/hooks/useThemedSurface";
import { useViewportNarrowerThan } from "./components/hooks/useViewportNarrowerThan";
import { useModalFocus } from "./components/hooks/useModalFocus";
import { applyBaseUrl, isStripeRedirect, stripStripeRedirectParams } from "./utils/mfeElement";
import {
  GuestCheckoutSettingsProvider,
  mergeGuestCheckoutSettings,
} from "./components/context/GuestCheckoutSettingsContext";
import { WidgetScrollProvider } from "./components/context/WidgetScrollContext";
import { WidgetMaximisedProvider } from "./components/context/WidgetMaximisedContext";
import type { WidgetAutoExpandRequest } from "./components/context/WidgetMaximisedContext";
import { WidgetTopBarProvider } from "./components/context/WidgetTopBarContext";
import { WidgetRequestProvider } from "./components/context/WidgetRequestContext";
import { PaymentRedirectProvider } from "./components/context/PaymentRedirectContext";

export { parseCssVariables };

const DEFAULT_MAX_WIDTH = 768;

/**
 * The stage's editable fields, in DOM order: text-like inputs, selects and
 * textareas — not hidden state, toggles or button-typed inputs. Used as the
 * maximised dialog's initial focus target, so opening lands where the guest
 * types next rather than on the back control ahead of it in the tab order.
 */
const STAGE_FIELD_SELECTOR = [
  'input:not([readonly], [type="hidden"], [type="checkbox"], [type="radio"], [type="button"], [type="submit"], [type="reset"], [type="file"])',
  "select",
  "textarea",
].join(",");

export { stripStripeRedirectParams };

interface BookingFormElementProps {
  /** Guest Checkout server origin used for JSON-RPC calls and asset URLs. */
  readonly baseUrl: string;
  /** CSS custom property overrides for venue styling. */
  readonly customStyles?: string;
  /** MFE attribute: colour scheme, `light`, `dark` or `auto`. */
  readonly theme?: string;
  /** MFE attribute: hide the offer selector. */
  readonly hideOffers?: string;
  /** MFE attribute: enable venue searching (accepted for parity; not yet surfaced in the UI). */
  readonly searchVenues?: string;
  /** MFE attribute: show the booking type selector before the date/covers step. */
  readonly showTypeFirst?: string;
  /** MFE attribute: automatically maximise the widget for pre-order bookings. */
  readonly autoExpandForPreorder?: string;
  /** MFE attribute: comma-separated venue IDs for multi-venue selection. */
  readonly venues?: string;
  /** MFE attribute: comma-separated booking type IDs to restrict the type selector. */
  readonly allowedTypes?: string;
  /** Maximum width in pixels when the booking form is maximised. */
  readonly maxWidth?: number;
  /**
   * Viewport width in pixels below which the widget hides itself and shows a
   * fixed "Book now" bar instead, which opens the booking journey full-screen.
   * Omitted (the default), the widget always renders inline.
   */
  readonly showBookNowSmallerThan?: number;
  readonly redirectUrl?: string;
}

function BookingFormElement({
  baseUrl,
  customStyles,
  theme,
  hideOffers,
  searchVenues,
  showTypeFirst,
  autoExpandForPreorder,
  venues,
  allowedTypes,
  maxWidth,
  showBookNowSmallerThan,
  redirectUrl,
}: BookingFormElementProps) {
  applyBaseUrl(baseUrl);
  const { t } = useTranslation();

  // For an embedded MFE, Stripe redirects back to the embedding page itself
  // (see stripeConnectForm.tsx), which reloads the whole page and re-mounts
  // this element from scratch. Detect the returned payment_intent/setup_intent
  // params once on mount and show the confirmation as a dismissible popup
  // rather than restarting the booking flow from its initial stage.
  const [isPaymentConfirmation] = useState(isStripeRedirect);
  const [showConfirmModal, setShowConfirmModal] = useState(isPaymentConfirmation);
  const [isMaximised, setIsMaximised] = useState(false);
  const autoExpandedRef = useRef(false);
  const rootRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  // State rather than a ref: the stages render into this node, so they have to
  // re-render once it exists.
  const [topBarSlot, setTopBarSlot] = useState<HTMLDivElement | null>(null);

  const handleMaximiseToggle = useCallback(() => {
    if (showConfirmModal) return;
    autoExpandedRef.current = false;
    setIsMaximised((current) => !current);
  }, [showConfirmModal]);

  const handleLauncherOpen = useCallback(() => {
    setIsMaximised(true);
  }, []);

  // Below the launcher breakpoint the widget gives up its place on the page to
  // the "Book now" bar, and its only visible state is the full-screen one. The
  // journey stays mounted throughout, so opening and closing the full-screen
  // view keeps the booking the guest has started.
  const isLauncherMode = useViewportNarrowerThan(showBookNowSmallerThan);
  // Below the breakpoint the collapsed root would hide the popup, so keep the widget full-screen behind it until dismissed.
  const isShowingConfirmationFullScreen = isLauncherMode && showConfirmModal;
  const isWidgetMaximised = isMaximised || isShowingConfirmationFullScreen;
  const isRootDialog = isWidgetMaximised && !showConfirmModal;
  const isFullScreen = isLauncherMode && isWidgetMaximised;
  const isCollapsed = isLauncherMode && !isWidgetMaximised;

  useEffect(() => {
    if (!isLauncherMode || !autoExpandedRef.current) return;
    autoExpandedRef.current = false;
    setIsMaximised(false);
  }, [isLauncherMode]);

  const handleAutoExpandRequest = useCallback(
    (request: WidgetAutoExpandRequest) => {
      if (request === "expand") {
        if (isLauncherMode || isMaximised) return;
        autoExpandedRef.current = true;
        setIsMaximised(true);
        return;
      }
      if (!autoExpandedRef.current) return;
      autoExpandedRef.current = false;
      setIsMaximised(false);
    },
    [isLauncherMode, isMaximised],
  );

  const handleConfirmModalClose = useCallback(() => {
    const cleanedSearch = stripStripeRedirectParams(window.location.search);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${cleanedSearch}${window.location.hash}`,
    );
    setShowConfirmModal(false);
  }, []);

  const previousShowConfirmModal = useRef(showConfirmModal);
  useEffect(() => {
    const wasShowingConfirmModal = previousShowConfirmModal.current;
    previousShowConfirmModal.current = showConfirmModal;
    if (wasShowingConfirmModal && !showConfirmModal && isLauncherMode) {
      launcherRef.current?.focus();
    }
  }, [showConfirmModal, isLauncherMode]);

  // `palette: "legacy"` keeps the widget's light rendering on the pre-token
  // palette, which design signed off as pixel-identical.
  const {
    widgetTheme,
    style: venueStyle,
    venueStyleKey,
    themeAttributes,
  } = useThemedSurface({
    theme,
    customStyles,
    palette: "legacy",
  });

  const maximisedWidth =
    typeof maxWidth === "number" && Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : DEFAULT_MAX_WIDTH;
  const appStyle = useMemo(
    () => ({
      ...venueStyle,
      // The full-screen state fills the window, so the maximised panel's width
      // cap must not apply to it.
      ...(isWidgetMaximised && !isFullScreen ? { maxWidth: `${maximisedWidth}px` } : {}),
    }),
    [isWidgetMaximised, isFullScreen, maximisedWidth, venueStyle],
  );

  const settings = useMemo(
    () =>
      mergeGuestCheckoutSettings({
        hideOffers,
        searchVenues,
        showTypeFirst,
        autoExpandForPreorder,
        venues,
        allowedTypes,
        isWidget: "true",
        theme: widgetTheme,
        venueStyleKey,
      }),
    [hideOffers, searchVenues, showTypeFirst, autoExpandForPreorder, venues, allowedTypes, widgetTheme, venueStyleKey],
  );

  useEffect(() => {
    initialiseBugsnag();
  }, []);

  useEffect(() => {
    if (!isWidgetMaximised || showConfirmModal) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isWidgetMaximised, showConfirmModal]);

  // Both maximised states cover the page, so the panel behaves as a modal
  // dialog: focus moves into it, Tab stays inside it, and closing it returns
  // focus to what opened it — the maximise control, or the launcher bar the
  // narrow-viewport state brings back.
  //
  // Opening mid-journey — the auto-expand the pre-order stages trigger — lands
  // focus on the stage's first editable field rather than the top bar's back
  // control, whose :focus ring then reads as though it were pressed. A stage
  // without a field (a spinner, the hosted payment fields) focuses the dialog
  // itself.
  useModalFocus(isMaximised, rootRef, launcherRef, STAGE_FIELD_SELECTOR);

  // The bar hugs the widget's own edges, while the stage inside it is inset by
  // both the journey's own padding and whatever padding the embedding page gives
  // the widget — neither of which the bar's offsets know about. Where the stage
  // actually starts is therefore measured and published for
  // `booking-form-mfe.scss`: how far the bar overlaps the stage, so a stage
  // whose controls are in the bar starts clear of it, and where the stage's left
  // edge is, so those controls line up with its heading and fields.
  useEffect(() => {
    const root = rootRef.current;
    const content = contentRef.current;
    const topBar = topBarRef.current;
    if (!root || !content || !topBar) return;

    const measure = () => {
      const bar = topBar.getBoundingClientRect();
      const flow = content.querySelector("#gc-booking-flow");
      const stage = (flow ?? content).getBoundingClientRect();
      content.style.setProperty("--gc-widget-top-bar-overlap", `${Math.max(0, bar.bottom - stage.top)}px`);
      // The flow's block padding is the one the widget overrides, so its inline
      // padding — the same `p-6` value — stands in for the stage's own inset.
      const inset = flow ? Number.parseFloat(getComputedStyle(flow).paddingInlineStart) : 0;
      content.style.setProperty("--gc-widget-flow-inset", `${inset}px`);
      topBar.style.setProperty("--gc-widget-top-bar-indent", `${stage.left + inset - bar.left}px`);
    };

    measure();
    // Environments without ResizeObserver keep the initial measurement.
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(topBar);
    // A stage change swaps the flow, whose inset the widget's spacing is taken
    // from, so the content wrapper is watched as well.
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Mount the booking form at /book while preserving the host page's query
  // string so venue_id and other seed parameters are available to BookingForm.
  // When returning from Stripe, strip its redirect params first so the fresh
  // booking form (shown behind, or after dismissing, the confirmation popup)
  // starts a new booking rather than seeding from the one just completed.
  const initialEntries = useMemo(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return [`/book${isPaymentConfirmation ? stripStripeRedirectParams(search) : search}`];
  }, [isPaymentConfirmation]);

  return (
    <PaymentRedirectProvider redirectUrl={redirectUrl}>
      <GuestCheckoutSettingsProvider value={settings}>
        {/*
        `gc-app-container` establishes a CSS container-query context sized to
        the widget itself (see layout.scss's `@container` rules). Guest
        Checkout is embedded as a web component on venue pages, so the
        embedding page's viewport can be desktop-sized even when the widget
        is rendered inside a narrow container; layout must respond to the
        widget's own width, not `window.innerWidth`.
      */}
        {/*
          `aria-modal` tells assistive technology the page behind the panel is
          unavailable, so it must not be operable by pointer either. The
          full-screen state covers the window by itself and needs no backdrop.
        */}
        {isWidgetMaximised && !isFullScreen && <div className="booking-form-mfe__backdrop" aria-hidden="true" />}
        {/*
          The widget root is a <dialog> kept permanently open so toggling the
          maximised view never remounts the journey inside it. Maximised, the
          panel covers the page, so it is announced and operated as a modal
          dialog rather than as part of the page beneath.
        */}
        <dialog
          ref={rootRef}
          open
          id="booking-form-app"
          className={`gc-root ${THEMED_SURFACE_CLASS} gc-app-container gcw:@container booking-form-mfe${isWidgetMaximised ? " booking-form-mfe--maximised" : ""}${isFullScreen ? " booking-form-mfe--full-screen" : ""}${isCollapsed ? " booking-form-mfe--collapsed" : ""}`}
          style={appStyle}
          {...themeAttributes}
          role={isRootDialog ? undefined : "none"}
          aria-modal={isRootDialog ? true : undefined}
          aria-label={isRootDialog ? t("booking_form_dialog_label", "Booking form") : undefined}
          tabIndex={isRootDialog ? -1 : undefined}
        >
          {/*
          The widget's top bar: the expand/shrink control, and a slot beside it
          the stages fill with the controls that belong on that line (the back
          control). Their position is measured from the widget's own edges, so
          the journey's padding can't drag them out of line.
        */}
          <div ref={topBarRef} className="booking-form-mfe__top-bar">
            <div ref={setTopBarSlot} className="booking-form-mfe__top-bar-slot" />
            <BookingFormMaximiseButton isMaximised={isWidgetMaximised} onToggle={handleMaximiseToggle} />
          </div>
          {/*
          The maximised panel scrolls this wrapper rather than itself, so the
          maximise button stays pinned to the panel's corner and its offsets are
          measured from the panel edge rather than from inside the scrollbar.
        */}
          {/* Only the maximised widget turns this region into an overflow scroll
          container, so it is keyboard-focusable in that state alone
          (WCAG scrollable-region-focusable). */}
          <div
            ref={contentRef}
            className="booking-form-mfe__content"
            tabIndex={isWidgetMaximised ? 0 : undefined} // NOSONAR — a scrollable region must be keyboard-focusable (WCAG 2.1.1)
          >
            <WidgetScrollProvider rootRef={rootRef} contentRef={contentRef} isMaximised={isWidgetMaximised}>
              <WidgetMaximisedProvider isMaximised={isWidgetMaximised} onAutoExpandRequest={handleAutoExpandRequest}>
                <WidgetTopBarProvider slot={topBarSlot}>
                  <WidgetRequestProvider>
                    <MemoryRouter initialEntries={initialEntries}>
                      <BookingForm />
                    </MemoryRouter>
                  </WidgetRequestProvider>
                </WidgetTopBarProvider>
              </WidgetMaximisedProvider>
            </WidgetScrollProvider>
          </div>
          {isPaymentConfirmation && (
            <Modal
              show={showConfirmModal}
              onHide={handleConfirmModalClose}
              centered
              dialogClassName="confirm-payment-modal"
              ariaLabel={t("booking_confirmation_modal_label", "Booking confirmation")}
            >
              <Modal.Header closeButton />
              <Modal.Body className="confirm-payment-modal__body">
                <ConfirmPayment />
              </Modal.Body>
            </Modal>
          )}
        </dialog>
        {isCollapsed && (
          <BookingFormLauncher
            buttonRef={launcherRef}
            themeAttributes={themeAttributes}
            style={venueStyle}
            onOpen={handleLauncherOpen}
          />
        )}
      </GuestCheckoutSettingsProvider>
    </PaymentRedirectProvider>
  );
}

const Component = r2wc(BookingFormElement, {
  props: {
    baseUrl: "string",
    customStyles: "string",
    theme: "string",
    hideOffers: "string",
    searchVenues: "string",
    showTypeFirst: "string",
    autoExpandForPreorder: "string",
    venues: "string",
    allowedTypes: "string",
    maxWidth: "number",
    showBookNowSmallerThan: "number",
    redirectUrl: "string",
  },
});

if (!window.customElements.get("booking-form")) {
  window.customElements.define("booking-form", Component);
}

export { BookingFormElement };
export default Component;
